import type { ComponentProps } from 'react';
import { Navigate } from 'react-router-dom';

import { type RouteConfig, RouteConfigProvider, useRouteConfig } from '@/context/route-config';
import type FuturesAppropriatenessTestLayout from '@/features/futures/layouts/appropriateness-test';
import { useProfileFuturesAppropriateness } from '@/hooks/queries/use-profile-futures-appropriateness';
import FuturesAppropriatenessTestResultFailedPage from '@/pages/futures/appropriateness-test-result-failed';
import FuturesAppropriatenessTestResultManualReviewPage from '@/pages/futures/appropriateness-test-result-manual-review';
import FuturesAppropriatenessTestResultPassedPage from '@/pages/futures/appropriateness-test-result-passed';
import LoadingPage from '@/pages/loading';
import { FuturesAppropriatenessResultStatus } from '@/services/api/profile/futures-appropriateness';

const ID = 'futures-result-page';

const LAYOUT_PROPS: ComponentProps<typeof FuturesAppropriatenessTestLayout> = {
  id: ID,
};

export interface FuturesAppropriatenessTestResultRouteConfig extends RouteConfig {
  appropriatenessTest: string;
}

const FuturesAppropriatenessTestResultContent = () => {
  const { isLoading, data: futuresAppropriateness } = useProfileFuturesAppropriateness();
  const routeConfig = useRouteConfig<FuturesAppropriatenessTestResultRouteConfig>();

  if (isLoading) return <LoadingPage />;

  if (!futuresAppropriateness || !('result' in futuresAppropriateness)) return <Navigate to={routeConfig.appropriatenessTest} replace />;

  return {
    [FuturesAppropriatenessResultStatus.PASSED]: <FuturesAppropriatenessTestResultPassedPage {...LAYOUT_PROPS} />,
    [FuturesAppropriatenessResultStatus.MANUAL_REVIEW]: <FuturesAppropriatenessTestResultManualReviewPage {...LAYOUT_PROPS} />,
    [FuturesAppropriatenessResultStatus.FAILED]: <FuturesAppropriatenessTestResultFailedPage {...LAYOUT_PROPS} />,
  }[futuresAppropriateness.result.status];
};
FuturesAppropriatenessTestResultContent.displayName = 'FuturesAppropriatenessTestResultContent';

function FuturesAppropriatenessTestResultPage() {
  return (
    <RouteConfigProvider
      config={{
        appropriatenessTest: '/futures/appropriateness-test',
      }}
    >
      <FuturesAppropriatenessTestResultContent />
    </RouteConfigProvider>
  );
}
FuturesAppropriatenessTestResultPage.displayName = 'FuturesAppropriatenessTestResultPage';

export default FuturesAppropriatenessTestResultPage;
